/* Store-wide navigation and shared offer display. */
(() => {
  class BootyliftHeader extends HTMLElement {
    connectedCallback() {
      this.menu = this.querySelector('.nav-dialog');
      this.menuTrigger = this.querySelector('[data-open-menu]');
      this.menuTrigger?.setAttribute('aria-controls', this.menu.id);
      this.menuTrigger?.setAttribute('aria-haspopup', 'dialog');
      this.menuTrigger?.setAttribute('aria-expanded', 'false');
      this.onClick = event => {
        const guide = event.target.closest('[data-open-guide]');
        const menu = event.target.closest('[data-open-menu]');
        if (guide || (menu && this.contains(menu))) {
          event.preventDefault();
          const guideId = {length: 'length-guide', chart: 'size-chart'}[guide?.dataset.openGuide] || 'size-guide';
          const dialog = guide ? (this.querySelector(`#${guideId}`) || this.querySelector('.guide-dialog')) : this.menu;
          const fromDialog = guide?.closest('dialog');
          if (guide && !fromDialog?.classList.contains('guide-dialog')) this.guideReturn = fromDialog === this.menu ? this.menuTrigger : guide;
          this.querySelectorAll('dialog[open]').forEach(open => open.close());
          dialog.showModal();
          if (dialog === this.menu) {
            clearTimeout(this.closeTimer);
            dialog.classList.remove('is-closing');
            document.body.classList.add('bl-nav-open');
            this.menuTrigger?.setAttribute('aria-expanded', 'true');
          }
        }
        const reviews = event.target.closest('[data-open-reviews]');
        if (reviews && !document.getElementById('bl-reviews-dialog')) document.querySelector('.bl-reviews')?.scrollIntoView({behavior: 'smooth', block: 'start'});
        const unit = event.target.closest('[data-guide-unit]');
        if (unit && this.contains(unit)) this.setGuideUnit(unit.dataset.guideUnit, true);
        const close = event.target.closest('[data-close]');
        if (close && this.contains(close)) this.closeDialog(close.closest('dialog'));
      };
      document.addEventListener('click', this.onClick);
      this.dialogEvents = new AbortController();
      const options = {signal: this.dialogEvents.signal};
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog(dialog);
        }, options);
        dialog.addEventListener('cancel', event => {
          event.preventDefault();
          this.closeDialog(dialog);
        }, options);
        dialog.addEventListener('close', () => {
          if (dialog !== this.menu) {
            if (dialog.classList.contains('guide-dialog')) this.guideReturn?.focus();
            return;
          }
          document.body.classList.remove('bl-nav-open');
          this.menuTrigger?.setAttribute('aria-expanded', 'false');
          dialog.classList.remove('is-closing');
        }, options);
      });
      try { this.setGuideUnit(localStorage.getItem('bootylift:guide-unit')); } catch (error) {}
      this.desktopQuery = matchMedia('(min-width: 701px)');
      this.onDesktop = event => { if (event.matches && this.menu.open) this.menu.close(); };
      this.desktopQuery.addEventListener('change', this.onDesktop);
      this.updateCountryFlag();
      this.setupCountrySwitch(options);
      const measure = () => {
        document.body.style.setProperty('--header-height', `${this.querySelector('header').offsetHeight}px`);
        document.body.style.setProperty('--header-group-height', `${this.offsetHeight}px`);
      };
      this.resizeObserver = new ResizeObserver(measure);
      this.resizeObserver.observe(this);
      measure();
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onClick);
      this.resizeObserver?.disconnect();
      this.dialogEvents?.abort();
      this.desktopQuery?.removeEventListener('change', this.onDesktop);
      clearTimeout(this.closeTimer);
      document.body.classList.remove('bl-nav-open');
    }
    setGuideUnit(unit, remember) {
      if (unit !== 'in' && unit !== 'cm') return;
      this.querySelectorAll('.bl-guide').forEach(guide => { guide.dataset.unit = unit; });
      this.querySelectorAll('[data-guide-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.guideUnit === unit)));
      if (remember) try { localStorage.setItem('bootylift:guide-unit', unit); } catch (error) {}
    }
    closeDialog(dialog) {
      if (dialog !== this.menu || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        dialog.close();
        return;
      }
      if (dialog.classList.contains('is-closing')) return;
      dialog.classList.add('is-closing');
      this.closeTimer = setTimeout(() => dialog.close(), 220);
    }
    flagFor(code) {
      return String.fromCodePoint(...[...code].map(letter => 127397 + letter.charCodeAt(0)));
    }
    // Flag button in the header opens a list of the store's countries; picking one submits Shopify's localization form.
    setupCountrySwitch(options) {
      const box = this.querySelector('[data-country-switch]');
      if (!box) return;
      box.querySelectorAll('[data-flag]').forEach(node => { node.textContent = this.flagFor(node.dataset.flag); });
      const summary = box.querySelector('summary');
      const sync = () => summary.setAttribute('aria-expanded', String(box.open));
      sync();
      box.addEventListener('toggle', sync, options);
      document.addEventListener('click', event => { if (box.open && !box.contains(event.target)) box.open = false; }, options);
      box.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !box.open) return;
        box.open = false;
        summary.focus();
      }, options);
      box.querySelector('form').addEventListener('submit', () => {
        try { localStorage.setItem('bootylift:country-chosen', '1'); } catch { /* The store still switches. */ }
      }, options);
    }
    async detectCountry() {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`, {
          headers: {Accept: 'application/json'}, signal: controller.signal
        });
        if (!response.ok) return null;
        const detected = (await response.json()).detected_values?.country;
        return /^[A-Z]{2}$/.test(detected?.handle || '') ? detected : null;
      } catch {
        return null;
      } finally {
        clearTimeout(timeout);
      }
    }
    async updateCountryFlag() {
      const flag = this.querySelector('[data-country-flag]');
      if (!flag) return;
      const box = flag.closest('[data-country-switch]');
      if (box) {
        flag.textContent = this.flagFor(flag.dataset.countryFlag);
        await this.assignDetectedCountry(box, flag);
        return;
      }
      // Single-country store: show the visitor's detected flag, as before.
      const detected = await this.detectCountry();
      const code = detected?.handle || flag.dataset.countryFlag;
      if (!/^[A-Z]{2}$/.test(code || '')) return;
      const name = detected ? detected.name || new Intl.DisplayNames([this.dataset.locale || 'en'], {type: 'region'}).of(code) : flag.getAttribute('aria-label');
      flag.textContent = this.flagFor(code);
      flag.setAttribute('aria-label', name || code);
      flag.setAttribute('title', name || code);
    }
    // Shopify keeps every new visitor in the default country, so the theme moves them to the country they are in.
    // Skipped once a visitor has picked a country themselves, and for countries the store does not sell to.
    async assignDetectedCountry(box, flag) {
      let chosen = false;
      try { chosen = localStorage.getItem('bootylift:country-chosen') === '1'; } catch { /* Treat as not chosen. */ }
      if (chosen) return;
      const detected = await this.detectCountry();
      const code = detected?.handle;
      if (!code || code === flag.dataset.countryFlag) return;
      const option = box.querySelector(`button[name="country_code"][value="${code}"]`);
      if (!option) return;
      const form = box.querySelector('form');
      const body = new FormData(form);
      const root = window.Shopify?.routes?.root || '/';
      body.set('country_code', code);
      body.set('return_to', `${root}cart.js`);
      try {
        const response = await fetch(form.action, {method: 'POST', body, headers: {Accept: 'application/json'}});
        if (!response.ok) return;
        const cart = await response.json().catch(() => null);
        // A different currency means every price on the page is stale.
        if (cart?.currency && window.Shopify?.currency?.active && cart.currency !== window.Shopify.currency.active) {
          window.location.reload();
          return;
        }
      } catch {
        return;
      }
      flag.dataset.countryFlag = code;
      flag.textContent = this.flagFor(code);
      const name = option.querySelector('.country-menu-name').textContent;
      box.querySelector('summary').setAttribute('aria-label', `Country: ${name}. Change country`);
      this.orderCountryMenu(box, code);
    }
    // Same order the server renders: current country, then US, Canada, Australia, UK, New Zealand, then the rest by name.
    orderCountryMenu(box, current) {
      const list = box.querySelector('.country-menu-list');
      const buttons = [...list.querySelectorAll('button')];
      buttons.forEach(button => button.toggleAttribute('aria-current', button.value === current));
      const popular = ['US', 'CA', 'AU', 'GB', 'NZ'];
      const byCode = code => buttons.find(button => button.value === code);
      const head = [byCode(current), ...popular.filter(code => code !== current).map(byCode)].filter(Boolean);
      const name = button => button.querySelector('.country-menu-name').textContent;
      const rest = buttons.filter(button => !head.includes(button)).sort((a, b) => name(a).localeCompare(name(b), this.dataset.locale || 'en'));
      list.replaceChildren(...head, ...rest);
    }
  }
  if (!customElements.get('bl-pdp-header')) customElements.define('bl-pdp-header', BootyliftHeader);
})();

// Session offer display. Backend pricing/eligibility must enforce the same expiry.
(() => {
  class BootyliftOfferCountdown extends HTMLElement {
    connectedCallback() {
      clearInterval(this.timer);
      this.observer?.disconnect();
          if (!this.dataset.deadline) try { const tz = 'America/New_York', parts = t => Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' }).formatToParts(t).map(x => [x.type, x.value])), offset = t => { const p = parts(t); return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - Math.floor(t / 1e3) * 1e3; }, now = Date.now(), p = parts(now), dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p.weekday), wall = Date.UTC(+p.year, +p.month - 1, +p.day + ((5 - dow + 7) % 7 || 7)); let d = wall - offset(now); d = wall - offset(d); this.dataset.deadline = new Date(d).toISOString(); } catch {} // Weekly offer: ends every Friday 00:00 US Eastern.
      const raw = this.dataset.deadline || '';
      if (raw) {
        this.deadline = /(?:Z|[+-]\d{2}:\d{2})$/.test(raw) ? Date.parse(raw) : NaN;
      } else {
        // A tab session survives reloads/navigation. Never renew an expired offer in that session.
        const key = 'bootylift:offer:18h26m19s:v2'; // New key: sessions holding the old 90-minute clock start on the new one.
        try {
          const stored = sessionStorage.getItem(key);
          if (stored === null) {
            this.deadline = Date.now() + (18 * 3600 + 26 * 60 + 19) * 1000;
            sessionStorage.setItem(key, String(this.deadline));
          } else {
            const parsed = Number(stored);
            this.deadline = Number.isSafeInteger(parsed) && parsed > 0 && parsed <= 8640000000000000 ? parsed : NaN;
          }
        } catch {
          // Without durable session storage, do not show a clock that resets on reload.
          this.deadline = NaN;
        }
      }
      if (Number.isFinite(this.deadline)) this.dataset.activeDeadline = new Date(this.deadline).toISOString();
      const announcement = this.parentElement.querySelector('.announcement');
      if (!Number.isFinite(this.deadline) || this.deadline <= Date.now() || !announcement) { this.hidden = true; return; }
      this.hidden = false;
      const staticClock = announcement.querySelector('[data-static-countdown]');
      const fallback = announcement.querySelector('[data-ann-fallback]');
      // The sale block under the PDP price shows the same deadline as the banner.
      const saleBlocks = () => document.querySelectorAll('[data-sale-timer]');
      const tick = () => {
        const remaining = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
        const values = {hours: Math.floor(remaining / 3600), minutes: Math.floor(remaining / 60) % 60, seconds: remaining % 60};
        for (const [unit, value] of Object.entries(values)) {
          const text = String(value).padStart(2, '0');
          this.querySelector(`[data-${unit}]`).textContent = text;
          if (staticClock) staticClock.querySelector(`[data-${unit}]`).textContent = text;
          for (const block of saleBlocks()) block.querySelector(`[data-${unit}]`).textContent = text;
        }
        if (staticClock) staticClock.hidden = !remaining;
        for (const block of saleBlocks()) block.hidden = !remaining;
        if (fallback) fallback.hidden = Boolean(remaining);
        const visible = remaining > 0 && Boolean(this.scrolled);
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        if (!remaining) { clearInterval(this.timer); this.observer?.disconnect(); }
      };
      this.observer = new IntersectionObserver(entries => {
        this.scrolled = !entries[0].isIntersecting && entries[0].boundingClientRect.bottom <= 0;
        tick();
      }, {threshold: 0});
      this.observer.observe(announcement);
      tick();
      this.timer = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); this.observer?.disconnect(); }
  }
  // Scroll bars for swipeable rows: [data-scrollbar] names the id of the row it follows.
  const wireScrollbar = bar => {
    const row = document.getElementById(bar.dataset.scrollbar);
    const thumb = bar.firstElementChild;
    if (!row || !thumb || bar.dataset.wired) return;
    bar.dataset.wired = 'true';
    let frame = 0;
    const update = () => {
      frame = 0;
      const overflow = row.scrollWidth - row.clientWidth;
      bar.hidden = overflow <= 1;
      if (bar.hidden) return;
      const size = row.clientWidth / row.scrollWidth;
      const progress = Math.min(1, Math.max(0, row.scrollLeft / overflow));
      bar.style.setProperty('--bl-scroll-size', `${size * 100}%`);
      bar.style.setProperty('--bl-scroll-offset', `${progress * (1 - size) / size * 100}%`);
    };
    const queue = () => { if (!frame) frame = requestAnimationFrame(update); };
    row.addEventListener('scroll', queue, {passive: true});
    new ResizeObserver(queue).observe(row);
    update();
  };
  const wireScrollbars = () => document.querySelectorAll('[data-scrollbar]').forEach(wireScrollbar);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wireScrollbars); else wireScrollbars();
  document.addEventListener('shopify:section:load', wireScrollbars);
  // Header: scrolls away with the page, hides while scrolling down and slides back in on any scroll up,
  // sitting under the offer banner when that is showing.
  const wireHeader = () => {
    const host = document.querySelector('bl-pdp-header');
    const header = host?.querySelector(':scope > header');
    if (!header || host.dataset.headroom) return;
    host.dataset.headroom = 'true';
    const banner = host.querySelector('bl-offer-countdown');
    let lastY = window.scrollY, frame = 0, height = header.offsetHeight;
    const home = () => header.getBoundingClientRect().top + window.scrollY;
    let start = home();
    const update = () => {
      frame = 0;
      // iOS reports overscroll (rubber-band) positions past both ends; a bounce is not a scroll direction.
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const y = Math.min(Math.max(window.scrollY, 0), max);
      const delta = y - lastY;
      const offset = banner?.classList.contains('is-visible') ? banner.offsetHeight : 0;
      header.style.setProperty('--bl-header-top', `${offset}px`);
      if (!header.classList.contains('is-pinned')) {
        if (y > start + height) {
          // Scrolled fully out of view: pin it already hidden, without animating it out of the page.
          host.style.paddingBottom = `${height}px`;
          header.classList.add('is-pinned', 'is-instant');
          header.getBoundingClientRect();
          header.classList.remove('is-instant');
        }
      } else if (start - y >= offset) {
        // Its place in the page is back where the pinned header sits, so it hands over without vanishing.
        // Unpinning any earlier dropped it behind the offer banner until the page reached the top.
        header.classList.remove('is-pinned', 'is-shown');
        host.style.paddingBottom = '';
      }
      const pinned = header.classList.contains('is-pinned');
      if (pinned && Math.abs(delta) > 6) header.classList.toggle('is-shown', delta < 0);
      if (Math.abs(delta) > 6 || !pinned) lastY = y;
    };
    window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(update); }, {passive: true});
    // The banner shows and hides on its own observer; re-place the header then too, not only on the next scroll.
    if (banner) new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(update); }).observe(banner, {attributes: true, attributeFilter: ['class']});
    window.addEventListener('resize', () => { if (!header.classList.contains('is-pinned')) { height = header.offsetHeight; start = home(); } }, {passive: true});
    update();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wireHeader); else wireHeader();
  if (!customElements.get('bl-offer-countdown')) customElements.define('bl-offer-countdown', BootyliftOfferCountdown);
})();
